import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Radio,
  Checkbox,
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import HTMLEditor from "../HtmlEditor/HtmlEditor";
import FillInTheBlankQuestion from "./FillInTheBlankQuestion";
import { useTranslation } from "react-i18next";
import { questionTypes } from "../../constants";
import OrderingQuestion from "./OrderingQuestion";
import MatchingQuestion from "./MatchingQuestion";
import { makeGetEntities } from "../../redux/selectors/selectors";
import { useSelector } from "react-redux";
import { Check, Close } from "@mui/icons-material";

const selector = makeGetEntities();

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4, 5, 2.5, 5),
    marginBottom: theme.spacing(3)
  },
  answerText: ({ selected }) => ({
    paddingLeft: theme.spacing(1),
    ...theme.typography.body1,
    color: selected ? theme.palette.text.primary : theme.palette.text.secondary
  }),
  choice: {
    padding: theme.spacing(1, 3, 1, 2),
    "&.Mui-selected": {
      backgroundColor: theme.palette.action.hover
    }
  },
  answersList: {
    margin: theme.spacing(2.5, 3, 0, 2.5)
  },
  pointsText: {
    lineHeight: 1.5
  }
}));

const getQuestionText = (t, questionType, text) => {
  if (questionType === questionTypes.fillBlanks) {
    return t("courseQuiz.fillInTheBlanksQuestionText");
  } else {
    return text;
  }
};

const ShortAnswer = ({ submitted, currentAnswer, onChange }) => {
  const [currentValue, setCurrentValue] = useState(currentAnswer);

  useEffect(() => {
    setCurrentValue(currentAnswer);
  }, [currentAnswer]);

  return (
    <Box mt={5} ml={3.5}>
      <TextField
        id="standard-textarea"
        defaultValue={currentAnswer}
        onBlur={() => onChange({ answer_text: currentValue })}
        onChange={event => setCurrentValue(event.target.value)}
        variant="filled"
        disabled={submitted}
        rows="4"
        fullWidth
        hiddenLabel
      />
    </Box>
  );
};

const LongAnswer = ({ submitted, allowHtml, currentAnswer, onChange }) => {
  const [currentValue, setCurrentValue] = useState(currentAnswer);

  const handleChange = newValue => {
    setCurrentValue(newValue);
    onChange({ answer_text: newValue });
  };

  return (
    <Box mt={5} ml={3.5}>
      {allowHtml ? (
        <HTMLEditor
          disableMargin
          height={189}
          plugins={["lists", "link", "code"]}
          menubar={false}
          value={currentValue}
          disabled={submitted}
          onChange={handleChange}
        />
      ) : (
        <TextField
          id="standard-textarea"
          value={currentValue}
          onBlur={() => onChange({ answer_text: currentValue })}
          onChange={event => setCurrentValue(event.target.value)}
          multiline
          variant="filled"
          disabled={submitted}
          rows="4"
          fullWidth
          hiddenLabel
        />
      )}
    </Box>
  );
};
const CourseQuizQuestionText = ({
  text,
  points,
  questionNumber,
  isSurvey,
  submitted,
  isCorrect
}) => {
  const classes = useStyles();
  const { palette } = useTheme();
  const { t } = useTranslation();
  let textColor = palette.secondary[500];
  if (submitted && !isSurvey) {
    textColor = isCorrect ? palette.success.main : palette.error.main;
  }
  return (
    <Box display="flex">
      {submitted && !isSurvey && (
        <Box mr={2.75} ml={-2} color={textColor}>
          {isCorrect ? <Check /> : <Close />}
        </Box>
      )}
      <Box pr={2}>
        <Typography
          color="primary"
          variant="body1"
        >{`${questionNumber}.`}</Typography>
      </Box>
      <Box display="flex" justifyContent="space-between" flex={1}>
        <Box pr={2}>
          <Typography
            color="textPrimary"
            variant="body1"
            display="inline"
            component="span"
          >
            <div dangerouslySetInnerHTML={{ __html: text }} />
          </Typography>
        </Box>
        <Box color={textColor}>
          {!isSurvey && (
            <Typography
              variant="body2"
              display="inline"
              className={classes.pointsText}
              noWrap
            >
              {t("courseQuiz.pointWithCount", { count: points })}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
CourseQuizQuestionText.defaultProps = {
  text: "",
  points: 1,
  questionNumber: 1
};

const CourseQuizQuestionAnswer = ({
  answer,
  onAnswerChange,
  selected,
  multiSelect,
  disabled
}) => {
  const classes = useStyles({ selected });
  return (
    <ListItem
      button
      onClick={() => onAnswerChange(answer)}
      selected={selected}
      className={classes.choice}
    >
      {multiSelect ? (
        <Checkbox checked={selected} color="primary" disabled={disabled} />
      ) : (
        <Radio checked={selected} color="primary" disabled={disabled} />
      )}
      <ListItemText
        primary={
          <div className={classes.answerText}>
            <div dangerouslySetInnerHTML={{ __html: answer.text }} />
          </div>
        }
      />
    </ListItem>
  );
};
const CourseQuizQuestionAnswers = ({
  answerChoices,
  multiSelect,
  onAnswerChange,
  selectedChoice,
  disableChange
}) => {
  const classes = useStyles();
  const answersList =
    answerChoices &&
    answerChoices.map((answer, index) => {
      const selected = selectedChoice
        ? selectedChoice.indexOf(answer.id) >= 0
        : false;
      return (
        <CourseQuizQuestionAnswer
          key={index}
          answer={answer}
          onAnswerChange={onAnswerChange}
          selected={selected}
          multiSelect={multiSelect}
          disabled={disableChange}
        />
      );
    });
  return <List className={classes.answersList}>{answersList}</List>;
};
const CourseQuizQuestion = ({
  text,
  questionType,
  answerChoices,
  matchChoices,
  matchClues,
  onAnswerChange,
  currentAnswer,
  points,
  isSurvey,
  questionNumber,
  questionId,
  allowHtml,
  fillInTheBlanks,
  submitted
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isCorrect = useSelector(({ learningCenter: { contentable } }) => {
    const question = selector(contentable, {
      type: "question",
      id: questionId
    });
    return question && question.learnerResponse
      ? question.learnerResponse.isCorrect
      : false;
  });

  function handleAnswerChange(answer) {
    if (submitted) {
      return;
    }
    onAnswerChange(questionType, answer, questionId); // answer is the user's response
  }

  return (
    <Paper className={classes.root}>
      <CourseQuizQuestionText
        text={getQuestionText(t, questionType, text)}
        points={points}
        questionNumber={questionNumber}
        isSurvey={isSurvey}
        submitted={submitted}
        isCorrect={isCorrect}
      />
      {questionType === questionTypes.fillBlanks ? (
        <FillInTheBlankQuestion
          fillInTheBlanks={fillInTheBlanks}
          onChange={handleAnswerChange}
          existingAnswers={currentAnswer}
          disabled={submitted}
        />
      ) : questionType === questionTypes.ordering ? (
        <OrderingQuestion
          orderingChoices={answerChoices}
          existingAnswers={currentAnswer}
          onAnswerChange={handleAnswerChange}
          disableChange={submitted}
        />
      ) : questionType === "long_answer" ? (
        <LongAnswer
          submitted={submitted}
          allowHtml={allowHtml}
          currentAnswer={currentAnswer}
          onChange={handleAnswerChange}
        />
      ) : questionType === questionTypes.matching ? (
        <MatchingQuestion
          matchClues={matchClues}
          matchChoices={matchChoices}
          existingAnswers={currentAnswer}
          onChange={handleAnswerChange}
          disabled={submitted}
        />
      ) : questionType === questionTypes.shortAnswer ? (
        <ShortAnswer
          submitted={submitted}
          currentAnswer={currentAnswer}
          onChange={handleAnswerChange}
        />
      ) : (
        <CourseQuizQuestionAnswers
          selectedChoice={currentAnswer}
          answerChoices={answerChoices}
          multiSelect={questionType === "multiple_select"}
          onAnswerChange={handleAnswerChange}
          disableChange={submitted}
        />
      )}
    </Paper>
  );
};

CourseQuizQuestion.propTypes = {
  /**
   * The answer choices
   */
  answerChoices: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string
    })
  ),
  currentAnswer: PropTypes.any,
  /**
   * The function that is called when the user chooses an answer
   */
  onAnswerChange: PropTypes.func,
  /**
   * The question number
   */
  questionNumber: PropTypes.number,
  /**
   * The number of points the question is worth
   */
  points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * The question text
   */
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * The question ID
   */
  questionId: PropTypes.number,
  questionType: PropTypes.string,
  allowHtml: PropTypes.bool, // for questionType === 'long_answer'
  submitted: PropTypes.bool,
  isSurvey: PropTypes.bool
};

export default CourseQuizQuestion;
